import {AppointmentRepository} from '@web-libs/features/appointments/domain/appointment.repository';
import {
    Appointment,
    AppointmentCategory,
    AppointmentInvitation,
    Specialization,
    Week
} from '@web-libs/features/appointments/domain/appointment.types';
import {HttpConcern} from '@web-libs/core/http/infrastructure/client-side/use-http.concern';
import {
    AppointmentCategoryDTO,
    AppointmentDTO,
    parseAppointmenCategoriesDTO,
    parseAppointmentCategoryDTO,
    parseAppointmentDTO,
    parseAppointmentDTOsTOInvitations,
    parseAppointmentsDTO,
    parseSpecializationsDTO,
    parseWeekDTO,
    SpecializationDTO,
    WeekDTO
} from '@web-libs/features/appointments/api/dto/appointment-dto.types';
import {AppointmentCategoryRepository} from '@web-libs/features/appointments/domain/category.repository';
import {toYYYYMMDDHHMMString} from '@web-libs/util/dates/date';
import {SpecializationRepository} from '@web-libs/features/appointments/domain/specialization.repository';
import {TrmzFileDTOConverter} from '@web-libs/features/files/trmz-file/api/trmz-file-dto-converter';

export const createAppointmentRepository = (http: HttpConcern,
                                            converter: TrmzFileDTOConverter): AppointmentRepository => {

    const getUpcomingAppointments = async (count: number): Promise<Appointment[]> => {
        return http.get<AppointmentDTO[]>('/appointments/upcoming',
            {queryParams: {'limit': count.toString()}})
            .then(
                (appointmentDTOS: AppointmentDTO[]) => {
                    return parseAppointmentsDTO(appointmentDTOS,
                        converter);
                }
            );
    };

    const getAppointmentsInCategory = async (categoryId: string,
                                             startDate: Date,
                                             endDate: Date,
                                             instId: string,
                                             specializationId?: string): Promise<Appointment[]> => {
        const newCategoryId = categoryId == 'allCategories' ? undefined : categoryId;
        return http.get<AppointmentDTO[]>('/appointments/bookable',
            {
                queryParams: {
                    categoryId: newCategoryId,
                    specializationId: specializationId,
                    startDate: toYYYYMMDDHHMMString(startDate,
                        '00:00'),
                    endDate: toYYYYMMDDHHMMString(endDate,
                        '23:59')
                }
            }).then(
            (appointmentsDTO: AppointmentDTO[]) => {
                return parseAppointmentsDTO(appointmentsDTO, converter,
                    'BookableAppointment');
            }
        );
    };

    const getOwnAppointments = async (startDate: Date,
                                      endDate: Date): Promise<Appointment[]> => {
        return http.get<AppointmentDTO[]>('/appointments',
            {
                queryParams: {
                    'startDate': toYYYYMMDDHHMMString(startDate,
                        '00:00'),
                    'endDate': toYYYYMMDDHHMMString(endDate,
                        '23:59'),
                }
            })
            .then(
                (appointmentsDTO: AppointmentDTO[]) => parseAppointmentsDTO(appointmentsDTO, converter)
            );
    };


    const getAppointment = async (appointmentId: string): Promise<Appointment> => {
        return http.get<AppointmentDTO>(`/appointments/${appointmentId}`)
            .then(
                (AppointmentDTO: AppointmentDTO) => parseAppointmentDTO(AppointmentDTO, converter)
            );
    };

    const getFirstWeekWithAppointments = async (categoryId: string,
                                                instId: string,
                                                specializationId?: string): Promise<Week> => {
        const newCategoryId = categoryId == 'allCategories' ? undefined : categoryId;
        return http.get<WeekDTO>('appointments/bookable/firstWeekWithAppointments',
            {queryParams: {eventCategoryId: newCategoryId, specializationId: specializationId}}).then(
            (weekDTO: WeekDTO) => {
                return parseWeekDTO(weekDTO);
            }
        );
    };

    const getUserId = async (): Promise<string> => {
        const user = await http.get<{ id: string }>('/users/self');

        return user.id;
    };

    type BookAppointmentDTO = {
        userIds: string[],
        participationTypeId?: string,
        categoryId?: string,
        registrationText?: string
    }

    const bookAppointment = async (appointmentId: string, participationTypeId?: string, categoryId?:string, reasonText?:string): Promise<void> => {
        const responseBody: BookAppointmentDTO= {
            userIds: [await getUserId()],
            ...(participationTypeId && { participationTypeId }),
            ...(categoryId && { categoryId }),
            ...(reasonText && { registrationText: reasonText })
        };
        await http.post(`/events/${appointmentId}/registrations`, responseBody);
    };

    const cancelAppointment = async (appointmentId: string): Promise<void> => {
        await http.post(`/events/${appointmentId}/registrations/cancel`,
            {userIds: [await getUserId()]});
    };


    const getOwnInvitations = async (instId: string): Promise<AppointmentInvitation[]> => {
        return http.get<AppointmentDTO[]>('/appointments/invitations',
            {queryParams: {instId: instId}})
            .then(
                (appointmentDTOS: AppointmentDTO[]) => {
                    return parseAppointmentDTOsTOInvitations(appointmentDTOS, converter);
                }
            );
    };


    const acceptInvitation = (invitation: AppointmentInvitation): Promise<void> => bookAppointment(invitation.appointment.id);


    const declineInvitation = (invitation: AppointmentInvitation): Promise<void> => cancelAppointment(invitation.appointment.id);

    const getCancellationReason = async (appointmentId: string): Promise<string | null> => {
        return http.get<{cancellationReason?: string}>(`/v2/events/${appointmentId}`)
            .then(response => response.cancellationReason || null)
            .catch(() => null);
    };

    return {
        getUpcomingAppointments,
        getAppointmentsInCategory,
        getOwnAppointments,
        getFirstWeekWithAppointments,
        bookAppointment,
        cancelAppointment,
        getOwnInvitations,
        acceptInvitation,
        declineInvitation,
        getAppointment,
        getCancellationReason
    };
};


export const createSpecializationRepository = (http: HttpConcern): SpecializationRepository => {

    const getSpecializations = async (instIds: string[]): Promise<Specialization[]> => {

        return http.get<SpecializationDTO[]>('/appointments/bookable/specializations')
            .then(
                (specializationsDTO) => {
                    return parseSpecializationsDTO(specializationsDTO);
                }
            );
    };

    return {
        getSpecializations
    };
};


export const createCategoryRepository = (http: HttpConcern): AppointmentCategoryRepository => {

    const getCategories = async (instId: string,
                                 specializationId?: string): Promise<AppointmentCategory[]> => {

        return http.get<AppointmentCategoryDTO[]>('/appointments/bookable/categories',
            {
                queryParams: {
                    'specializationId': specializationId
                }
            })
            .then((appointmentCategoriesDTO: AppointmentCategoryDTO[]) => {
                return parseAppointmenCategoriesDTO(appointmentCategoriesDTO);
            });
    };
    const getCategory = async (id: string): Promise<AppointmentCategory> => {
        return http.get<AppointmentCategoryDTO>(`categories/${id}`).then(
            (appointmentCategoryDTO: AppointmentCategoryDTO) => {
                return parseAppointmentCategoryDTO((appointmentCategoryDTO));
            }
        );
    };

    return {
        getCategories,
        getCategory
    };
};
